import { normalizeFieldFormat, normalizeFieldFormatRecord } from '@/lib/fieldFormat'
import { apiRequest, apiRequestBlob, ApiRequestError } from '@/lib/httpClient'
import type {
  AgentBuilderCatalogView,
  AgentBuilderChatMessage,
  AgentBuilderDashboard,
  AgentBuilderDashboardSchedule,
  AgentBuilderDashboardScheduleUpsertRequest,
  AgentBuilderDashboardVersion,
  AgentBuilderDashboardView,
  AgentBuilderFieldOption,
  AgentBuilderFilterOption,
  AgentBuilderGroupInsightPayload,
  AgentBuilderInsightDto,
  AgentBuilderMemory,
  AgentBuilderMemoryDashboardScope,
  AgentBuilderMemoryUserScope,
  AgentBuilderScheduleRecipientOption,
  AgentBuilderWidgetInsightPayload,
  DashboardAnalysisContext,
  DashboardDefinition,
  DashboardWidgetType,
  PendingScheduleConfirmation,
  SavedDashboardViewState,
  ScheduleImpactSchedule,
  ScheduleImpactWidget,
} from './types'
import { sanitizeDashboardSection } from './sections'
import type { DrilldownHierarchyNode } from './drilldownHierarchy'

import {
  asArray,
  asBoolean,
  asIsoDateOrNull,
  asNumber,
  asObject,
  asRowArray,
  asString,
  drilldownWidgetRequestBody,
  exportWidgetRequestBody,
  getRawValue,
  hydrateWidgetRequestBody,
  slicerOptionsRequestBody,
  toHydratedWidgetResult,
  toSlicerOptions,
  type DrilldownWidgetInput,
  type ExportWidgetDataInput,
  type FetchSlicerOptionsInput,
  type HydratedWidgetResult,
  mapDatePresetOptions,
  type HydrateWidgetInput,
  type Raw,
} from './widgetData'

export type { AgentBuilderDashboardScheduleUpsertRequest } from './types'
export type {
  AgentBuilderComparisonPeriodInput,
  DrilldownWidgetInput,
  ExportWidgetDataInput,
  HydratedWidgetResult,
  HydrateWidgetInput,
} from './widgetData'
export { buildSlicerOptionsRequestFilters, mapDatePresetOptions } from './widgetData'

const HYDRATE_WIDGET_RETRY_DELAYS_MS = [300, 800]

function delay(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}

function isRetryableHydrateError(error: unknown) {
  if (error instanceof ApiRequestError) {
    return error.status === 408 || error.status === 429 || error.status >= 500
  }

  if (error instanceof DOMException) {
    return error.name === 'AbortError' || error.name === 'TimeoutError'
  }

  return error instanceof TypeError
}

async function withHydrateWidgetRetry<T>(request: () => Promise<T>): Promise<T> {
  let lastError: unknown

  for (let attempt = 0; attempt <= HYDRATE_WIDGET_RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      return await request()
    } catch (error) {
      lastError = error
      const retryDelay = HYDRATE_WIDGET_RETRY_DELAYS_MS[attempt]
      if (retryDelay == null || !isRetryableHydrateError(error)) {
        throw error
      }
      await delay(retryDelay)
    }
  }

  throw lastError
}

function asStringList(value: unknown): string[] {
  return asArray(value)
    .filter((item): item is string | number => typeof item === 'string' || typeof item === 'number')
    .map((item) => String(item).trim())
    .filter(Boolean)
}

function mapInsightDto(raw: Raw): AgentBuilderInsightDto {
  return {
    insight: asString(raw['insight'] ?? raw['Insight']),
    keyFindings: asStringList(raw['keyFindings'] ?? raw['KeyFindings']),
    recommendations: asStringList(raw['recommendations'] ?? raw['Recommendations']),
    fromCache: Boolean(raw['fromCache'] ?? raw['FromCache']),
  }
}

function asJsonString(value: unknown): string | null {
  if (typeof value === 'string') return value.trim() ? value : null
  if (value === null || value === undefined) return null
  return JSON.stringify(value)
}

function mapComputedResult(value: unknown): AgentBuilderChatMessage['computedResult'] {
  const raw = asObject(value)
  if (!raw) return null

  const columns = asArray<string>(raw['columns'] ?? raw['Columns']).filter((item): item is string => typeof item === 'string')
  const rows = asRowArray(raw['rows'] ?? raw['Rows'])
  const valueResult = raw['value'] ?? raw['Value']
  const columnFormats = normalizeFieldFormatRecord(raw['columnFormats'] ?? raw['ColumnFormats'])
  const valueFormat = normalizeFieldFormat(raw['valueFormat'] ?? raw['ValueFormat'])

  if (rows.length > 0 && columns.length > 0) {
    return {
      columns,
      rows,
      value: valueResult as string | number | null | undefined,
      columnFormats,
      valueFormat,
    }
  }

  if (valueResult !== undefined && valueResult !== null) {
    return {
      value: valueResult as string | number | null,
      valueFormat,
    }
  }

  return null
}

function mapFieldOption(raw: Raw): AgentBuilderFieldOption {
  const type = asString(raw['type'] ?? raw['Type'])
  return {
    name: asString(raw['name'] ?? raw['Name']),
    value: asString(raw['value'] ?? raw['Value']),
    type,
    isCustom: Boolean(raw['isCustom'] ?? raw['IsCustom']),
    format: normalizeFieldFormat(raw['format'] ?? raw['Format'], type),
  }
}

function mapFilterOptions(value: unknown): AgentBuilderFilterOption[] {
  return asArray<Raw>(value).map((raw) => ({
    name: asString(raw['name'] ?? raw['Name']),
    value: asString(raw['value'] ?? raw['Value']),
  }))
}

export function mapCatalog(payload: Raw): AgentBuilderCatalogView {
  return {
    fieldDimensionOptions: asArray<Raw>(payload['fieldDimensionOptions'] ?? payload['FieldDimensionOptions']).map(mapFieldOption),
    fieldMeasureOptions: asArray<Raw>(payload['fieldMeasureOptions'] ?? payload['FieldMeasureOptions']).map(mapFieldOption),
    numberFilterOptions: mapFilterOptions(payload['numberFilterOptions'] ?? payload['NumberFilterOptions']),
    dateConditionFilterOptions: mapFilterOptions(payload['dateConditionFilterOptions'] ?? payload['DateConditionFilterOptions']),
    dateFreqFilterOptions: mapFilterOptions(payload['dateFreqFilterOptions'] ?? payload['DateFreqFilterOptions']),
    booleanFilterOptions: mapFilterOptions(payload['booleanFilterOptions'] ?? payload['BooleanFilterOptions']),
    booleanStringFilterOptions: mapFilterOptions(payload['booleanStringFilterOptions'] ?? payload['BooleanStringFilterOptions']),
    stringFilterOptions: mapFilterOptions(payload['stringFilterOptions'] ?? payload['StringFilterOptions']),
    datePresetOptions: mapDatePresetOptions(payload['datePresetOptions'] ?? payload['DatePresetOptions']),
    datePresetsAsOf: asIsoDateOrNull(payload['datePresetsAsOf'] ?? payload['DatePresetsAsOf']),
  }
}

function mapDashboard(payload: Raw): AgentBuilderDashboard {
  const versionId = asString(payload['versionId'] ?? payload['VersionId'] ?? payload['id'] ?? payload['Id'])
  return {
    id: versionId,
    versionId,
    rootId: asString(payload['rootId'] ?? payload['RootId']) || null,
    rootDashboardId: asString(payload['rootDashboardId'] ?? payload['RootDashboardId'] ?? payload['rootId'] ?? payload['RootId']) || null,
    dashboardId: asString(payload['dashboardId'] ?? payload['DashboardId']) || null,
    clientId: asString(payload['clientId'] ?? payload['ClientId']),
    platformTypeId: asNumber(payload['platformTypeId'] ?? payload['PlatformTypeId']),
    section: asString(payload['section'] ?? payload['Section']) || null,
    title: asString(payload['title'] ?? payload['Title']),
    prompt: asString(payload['prompt'] ?? payload['Prompt']) || null,
    dashboardJson: asString(payload['dashboardJson'] ?? payload['DashboardJson']) || null,
    versionNumber: asNumber(payload['versionNumber'] ?? payload['VersionNumber']),
    isCurrent: asBoolean(payload['isCurrent'] ?? payload['IsCurrent']),
    isDraft: asBoolean(payload['isDraft'] ?? payload['IsDraft']),
    isFavorite: asBoolean(payload['isFavorite'] ?? payload['IsFavorite']),
    insertUserId: asString(payload['insertUserId'] ?? payload['InsertUserId']) || null,
    insertDate: asString(payload['insertDate'] ?? payload['InsertDate']) || null,
    updateDate: asString(payload['updateDate'] ?? payload['UpdateDate']) || null,
    defaultView: mapOptionalDashboardView(payload['defaultView'] ?? payload['DefaultView']),
  }
}

function parseSavedDashboardViewState(value: unknown): SavedDashboardViewState | null {
  if (typeof value !== 'string' || !value) return null
  try {
    const parsed: unknown = JSON.parse(value)
    const raw = asObject(parsed)
    if (!raw) return null
    return { ...raw, schemaVersion: asNumber(raw['schemaVersion']) || 1 } as SavedDashboardViewState
  } catch {
    return null
  }
}

function mapDashboardView(payload: Raw): AgentBuilderDashboardView {
  return {
    viewId: asString(payload['viewId'] ?? payload['ViewId']),
    dashboardId: asString(payload['dashboardId'] ?? payload['DashboardId']),
    name: asString(payload['name'] ?? payload['Name']),
    isDefault: asBoolean(payload['isDefault'] ?? payload['IsDefault']),
    state: parseSavedDashboardViewState(payload['stateJson'] ?? payload['StateJson']),
    schemaVersion: asNumber(payload['schemaVersion'] ?? payload['SchemaVersion']) || 1,
    insertDate: asString(payload['insertDate'] ?? payload['InsertDate']) || null,
    updateDate: asString(payload['updateDate'] ?? payload['UpdateDate']) || null,
  }
}

function mapOptionalDashboardView(value: unknown): AgentBuilderDashboardView | null {
  const raw = asObject(value)
  return raw ? mapDashboardView(raw) : null
}

function mapMemory(payload: Raw): AgentBuilderMemory {
  const memoryId = asString(payload['memoryId'] ?? payload['MemoryId'] ?? payload['id'] ?? payload['Id'])
  const userId = asString(payload['userId'] ?? payload['UserId']) || null
  const rootDashboardId = asString(payload['rootDashboardId'] ?? payload['RootDashboardId']) || null
  const userScope = asString(payload['userScope'] ?? payload['UserScope'])
  const dashboardScope = asString(payload['dashboardScope'] ?? payload['DashboardScope'])
  return {
    id: memoryId,
    memoryId,
    clientId: asString(payload['clientId'] ?? payload['ClientId']),
    userId,
    rootDashboardId,
    userScope: userScope === 'entire_client' || !userId ? 'entire_client' : 'current_user',
    dashboardScope: dashboardScope === 'all_dashboards' || !rootDashboardId ? 'all_dashboards' : 'this_dashboard',
    memory: asString(payload['memory'] ?? payload['Memory']),
    insertDate: asString(payload['insertDate'] ?? payload['InsertDate']) || null,
    updateDate: asString(payload['updateDate'] ?? payload['UpdateDate']) || null,
  }
}

function mapScheduleRecipient(value: unknown) {
  const raw = asObject(value) ?? {}
  return {
    id: asString(raw['id'] ?? raw['Id']),
    type: asNumber(raw['type'] ?? raw['Type']),
  }
}

function mapDashboardSchedule(payload: Raw): AgentBuilderDashboardSchedule {
  const scheduleId = asString(payload['agentBuilderDashboardScheduleId'] ?? payload['AgentBuilderDashboardScheduleId'])
  const rawIncludedWidgetIds = getRawValue(payload, 'includedWidgetIds', 'IncludedWidgetIds')
  const includedWidgetIds = rawIncludedWidgetIds === null ? null
    : Array.isArray(rawIncludedWidgetIds) ? rawIncludedWidgetIds.map((id) => String(id).trim()).filter(Boolean)
    : undefined
  return {
    agentBuilderDashboardScheduleId: scheduleId,
    agentBuilderDashboardRootId: asString(payload['agentBuilderDashboardRootId'] ?? payload['AgentBuilderDashboardRootId']),
    clientId: asString(payload['clientId'] ?? payload['ClientId']),
    dashboardTitle: asString(payload['dashboardTitle'] ?? payload['DashboardTitle']) || undefined,
    schedule: asString(payload['schedule'] ?? payload['Schedule']),
    scheduleTime: asString(payload['scheduleTime'] ?? payload['ScheduleTime']),
    timeZoneId: asString(payload['timeZoneId'] ?? payload['TimeZoneId']) || undefined,
    displayTime: asString(payload['displayTime'] ?? payload['DisplayTime']) || undefined,
    recipients: asArray(payload['recipients'] ?? payload['Recipients']).map(mapScheduleRecipient),
    bcc: asArray<string>(payload['bcc'] ?? payload['Bcc']).filter((item): item is string => typeof item === 'string'),
    subject: asString(payload['subject'] ?? payload['Subject']),
    body: asString(payload['body'] ?? payload['Body']),
    includedWidgetIds,
  }
}

function mapScheduleRecipientOption(payload: Raw): AgentBuilderScheduleRecipientOption {
  return {
    id: asString(payload['id'] ?? payload['Id']),
    name: asString(payload['name'] ?? payload['Name']),
    type: asNumber(payload['type'] ?? payload['Type']),
  }
}

export async function getAgentBuilderCatalog(clientId: string, platformTypeId: number): Promise<AgentBuilderCatalogView> {
  const payload = await apiRequest<Raw>('agentBuilder/ui/catalog', {
    query: { clientId, platformTypeId },
  })
  return mapCatalog(payload)
}

function mapPendingScheduleConfirmation(value: unknown): PendingScheduleConfirmation | undefined {
  const raw = asObject(value)
  if (!raw) return undefined

  const status = asString(raw['status'] ?? raw['Status']).toLowerCase()
  if (status !== 'pending' && status !== 'accepted' && status !== 'declined' && status !== 'stale') {
    return undefined
  }

  const kind = asString(raw['kind'] ?? raw['Kind']).toLowerCase()
  if (kind !== 'update' && kind !== 'replace') {
    return undefined
  }

  // Entries with an unknown change/effect are dropped rather than rendered with an empty tag.
  const widgets = asArray<Raw>(raw['widgets'] ?? raw['Widgets']).flatMap((item): ScheduleImpactWidget[] => {
    const change = asString(item['change'] ?? item['Change'])
    if (change !== 'updated' && change !== 'retyped' && change !== 'removed') return []
    const fromTypeStr = asString(item['fromType'] ?? item['FromType']).trim()
    const toTypeStr = asString(item['toType'] ?? item['ToType']).trim()
    return [{
      widgetId: asString(item['widgetId'] ?? item['WidgetId']),
      title: asString(item['title'] ?? item['Title']),
      change,
      fromType: fromTypeStr ? (fromTypeStr as DashboardWidgetType) : undefined,
      toType: toTypeStr ? (toTypeStr as DashboardWidgetType) : undefined,
      newWidgetId: asString(item['newWidgetId'] ?? item['NewWidgetId']) || undefined,
    }]
  })

  const schedules = asArray<Raw>(raw['schedules'] ?? raw['Schedules']).flatMap((item): ScheduleImpactSchedule[] => {
    const effect = asString(item['effect'] ?? item['Effect'])
    if (effect !== 'continues' && effect !== 'deleted' && effect !== 'widgetRemoved') return []
    return [{
      scheduleId: asString(item['scheduleId'] ?? item['ScheduleId']),
      subject: asString(item['subject'] ?? item['Subject']),
      schedule: asString(item['schedule'] ?? item['Schedule']),
      scheduleTime: asString(item['scheduleTime'] ?? item['ScheduleTime']),
      effect,
    }]
  })

  return {
    status: status as 'pending' | 'accepted' | 'declined' | 'stale',
    kind: kind as 'update' | 'replace',
    widgets,
    schedules,
    dashboardFiltersChanged: asBoolean(raw['dashboardFiltersChanged'] ?? raw['DashboardFiltersChanged']),
  }
}

export type SendAgentBuilderChatMessagePayload = {
  agentBuilderChatId: string
  dashboardId?: string | null
  currentDashboardJson: string
  dashboardAnalysisContext?: DashboardAnalysisContext | null
  question: string
  messages: AgentBuilderChatMessage[]
  clientId: string
  platformTypeId: number
  section?: string | null
  isDraft?: boolean
  route?: 'dashboard_creation' | 'dashboard_generation' | 'dashboard_modification' | 'data_analysis' | 'direct_answer'
}

export type SendAgentBuilderChatMessageResponse = {
  agentBuilderChatId: string
  status: 'queued' | 'processing' | 'completed' | 'failed'
}

export type AgentBuilderChatResult = {
  message: AgentBuilderChatMessage
  updatedDashboardJson: string | null
  rootDashboardId: string | null
  dashboardId: string | null
  versionId: string | null
}

export type AgentBuilderChatStatusResponse = {
  agentBuilderChatId: string
  status: 'queued' | 'processing' | 'completed' | 'failed'
  result: AgentBuilderChatResult | null
  errorMessage: string | null
  insertDate: string | null
  startedDate: string | null
  completedDate: string | null
}

export type AgentBuilderClientUserEmailOption = {
  email: string
  fullName: string
}

export type AgentBuilderSettings = {
  clientId: string
  impersonationEmail: string | null
}

export async function sendAgentBuilderChatMessage(payload: SendAgentBuilderChatMessagePayload): Promise<SendAgentBuilderChatMessageResponse> {
  const raw = await apiRequest<Raw>('agentBuilder/ui/chat/message', { method: 'POST', body: payload })
  return {
    agentBuilderChatId: asString(raw['agentBuilderChatId'] ?? raw['AgentBuilderChatId']),
    status: (asString(raw['status'] ?? raw['Status']) || 'queued') as SendAgentBuilderChatMessageResponse['status'],
  }
}

function mapChatStatusResponse(raw: Raw): AgentBuilderChatStatusResponse {
  const result = asObject(raw['result'] ?? raw['Result'])
  const message = asObject(result?.['message'] ?? result?.['Message']) ?? {}
  return {
    agentBuilderChatId: asString(raw['agentBuilderChatId'] ?? raw['AgentBuilderChatId']),
    status: (asString(raw['status'] ?? raw['Status']) || 'queued') as AgentBuilderChatStatusResponse['status'],
    result: result
      ? {
          message: {
            messageId: asString(message['messageId'] ?? message['MessageId']),
            agentBuilderChatId: asString(raw['agentBuilderChatId'] ?? raw['AgentBuilderChatId']),
            role: asString(message['role'] ?? message['Role']) === 'user' ? 'user' : 'assistant',
            content: asString(message['content'] ?? message['Content']),
            computedResult: mapComputedResult(message['computedResult'] ?? message['ComputedResult']),
            // Sent beside the message; kept on it so the card survives the session-storage restore.
            pendingScheduleConfirmation: mapPendingScheduleConfirmation(
              result['pendingScheduleConfirmation'] ?? result['PendingScheduleConfirmation'],
            ),
          },
          updatedDashboardJson: asJsonString(result['updatedDashboardJson'] ?? result['UpdatedDashboardJson']),
          rootDashboardId: asString(result['rootDashboardId'] ?? result['RootDashboardId']) || null,
          dashboardId: asString(result['dashboardId'] ?? result['DashboardId']) || null,
          versionId: asString(result['versionId'] ?? result['VersionId']) || null,
        }
      : null,
    errorMessage: asString(raw['errorMessage'] ?? raw['ErrorMessage']) || null,
    insertDate: asString(raw['insertDate'] ?? raw['InsertDate']) || null,
    startedDate: asString(raw['startedDate'] ?? raw['StartedDate']) || null,
    completedDate: asString(raw['completedDate'] ?? raw['CompletedDate']) || null,
  }
}

export async function getAgentBuilderChatMessage(agentBuilderChatId: string): Promise<AgentBuilderChatStatusResponse> {
  if (!agentBuilderChatId || agentBuilderChatId === 'undefined') {
    throw new Error('agentBuilderChatId is required')
  }
  return mapChatStatusResponse(await apiRequest<Raw>(`agentBuilder/ui/chat/${agentBuilderChatId}`))
}

export type SaveAgentBuilderDashboardPayload = {
  dashboardId?: string | null
  clientId: string
  platformTypeId: number
  section?: string | null
  title: string
  prompt: string
  dashboardJson: string
  isDraft?: boolean
}

export type CreateAgentBuilderMemoryPayload = {
  clientId: string
  rootDashboardId?: string | null
  userScope: AgentBuilderMemoryUserScope
  dashboardScope: AgentBuilderMemoryDashboardScope
  memory: string
}

export type UpdateAgentBuilderMemoryPayload = CreateAgentBuilderMemoryPayload & {
  memoryId: string
}

export type DeleteAgentBuilderMemoryPayload = {
  clientId: string
  memoryId: string
}

export async function getAgentBuilderMemories(clientId: string, rootDashboardId?: string | null): Promise<AgentBuilderMemory[]> {
  const raw = await apiRequest<Raw[]>('agentBuilder/ui/memories', {
    query: rootDashboardId ? { clientId, rootDashboardId } : { clientId },
  })
  return asArray<Raw>(raw).map(mapMemory)
}

export async function createAgentBuilderMemory(payload: CreateAgentBuilderMemoryPayload): Promise<AgentBuilderMemory> {
  const raw = await apiRequest<Raw>('agentBuilder/ui/memories', { method: 'POST', body: payload })
  return mapMemory(raw)
}

export async function updateAgentBuilderMemory(payload: UpdateAgentBuilderMemoryPayload): Promise<AgentBuilderMemory> {
  const raw = await apiRequest<Raw>(`agentBuilder/ui/memories/${payload.memoryId}`, {
    method: 'PUT',
    body: {
      clientId: payload.clientId,
      rootDashboardId: payload.rootDashboardId,
      userScope: payload.userScope,
      dashboardScope: payload.dashboardScope,
      memory: payload.memory,
    },
  })
  return mapMemory(raw)
}

export async function deleteAgentBuilderMemory(payload: DeleteAgentBuilderMemoryPayload): Promise<void> {
  await apiRequest<Raw>(`agentBuilder/ui/memories/${payload.memoryId}`, {
    method: 'DELETE',
    query: { clientId: payload.clientId },
  })
}

export type CreateAgentBuilderDashboardFromPromptPayload = {
  clientId: string
  platformTypeId: number
  section?: string | null
  question: string
  isDraft?: boolean
}

export type CreateAgentBuilderDashboardFromJsonPayload = {
  clientId: string
  platformTypeId: number
  section?: string | null
  title?: string | null
  dashboardJson: string
  isDraft?: boolean
}

export type RestoreAgentBuilderDashboardVersionPayload = {
  clientId: string
  versionId: string
}

export type PublishAgentBuilderDashboardVersionPayload = RestoreAgentBuilderDashboardVersionPayload

export type UnpublishAgentBuilderDashboardVersionPayload = RestoreAgentBuilderDashboardVersionPayload

export type DeleteAgentBuilderDashboardVersionPayload = RestoreAgentBuilderDashboardVersionPayload & {
  dashboardId?: string | null
  platformTypeId?: number | null
}

export type DeleteAgentBuilderDashboardPayload = {
  clientId: string
  rootId: string
  platformTypeId?: number | null
}

export async function getAgentBuilderDashboards(clientId: string, platformTypeId?: number | null): Promise<AgentBuilderDashboard[]> {
  const params = new URLSearchParams({ clientId })
  if (platformTypeId) params.set('platformTypeId', String(platformTypeId))
  const result = await apiRequest<Raw[]>(`agentBuilder/ui/dashboards?${params.toString()}`)
  return Array.isArray(result) ? result.map(mapDashboard) : []
}

export async function getAgentBuilderClientUserEmailOptions(clientId: string): Promise<AgentBuilderClientUserEmailOption[]> {
  const result = await apiRequest<Raw[]>('agentBuilder/ui/client-user-email-options', {
    query: { clientId },
  })
  return asArray<Raw>(result).map((item) => ({
    email: asString(item['email'] ?? item['Email']),
    fullName: asString(item['fullName'] ?? item['FullName']),
  })).filter((item) => item.email)
}

export async function getAgentBuilderSettings(clientId: string): Promise<AgentBuilderSettings> {
  const raw = await apiRequest<Raw>('agentBuilder/ui/settings', {
    query: { clientId },
  })
  return {
    clientId: asString(raw['clientId'] ?? raw['ClientId']) || clientId,
    impersonationEmail: asString(raw['impersonationEmail'] ?? raw['ImpersonationEmail']) || null,
  }
}

export type AgentBuilderAccessStatus = {
  hasValidPlatform: boolean
  hasValidUserGroup: boolean
  canAccess: boolean
}

export async function getAgentBuilderAccessStatus(clientId: string): Promise<AgentBuilderAccessStatus> {
  const raw = await apiRequest<Raw>('agentBuilder/ui/access-status', {
    query: { clientId },
  })
  const hasValidPlatform = (raw['hasValidPlatform'] ?? raw['HasValidPlatform']) === true
  const hasValidUserGroup = (raw['hasValidUserGroup'] ?? raw['HasValidUserGroup']) === true
  return {
    hasValidPlatform,
    hasValidUserGroup,
    canAccess: (raw['canAccess'] ?? raw['CanAccess'] ?? (hasValidPlatform && hasValidUserGroup)) === true,
  }
}

export type UpdateAgentBuilderSettingsPayload = {
  clientId: string
  impersonationEmail?: string | null
}

export async function updateAgentBuilderSettings(payload: UpdateAgentBuilderSettingsPayload): Promise<AgentBuilderSettings> {
  const body: Record<string, unknown> = { clientId: payload.clientId }
  if (payload.impersonationEmail !== undefined) {
    body['impersonationEmail'] = payload.impersonationEmail ?? ''
  }
  const raw = await apiRequest<Raw>('agentBuilder/ui/settings', {
    method: 'PUT',
    body,
  })
  return {
    clientId: asString(raw['clientId'] ?? raw['ClientId']) || payload.clientId,
    impersonationEmail: asString(raw['impersonationEmail'] ?? raw['ImpersonationEmail']) || null,
  }
}

export async function getAgentBuilderDashboard(dashboardId: string, clientId: string): Promise<AgentBuilderDashboard | null> {
  if (!dashboardId || !clientId) return null
  const response = await apiRequest<Raw>(`agentBuilder/ui/dashboards/${dashboardId}`, {
    query: { clientId },
  })
  return response ? mapDashboard(response) : null
}

export async function getAgentBuilderDashboardVersions(clientId: string, dashboardId?: string | null): Promise<AgentBuilderDashboardVersion[]> {
  const params = new URLSearchParams({ clientId })
  if (dashboardId) {
    params.set('dashboardId', dashboardId)
  }

  const result = await apiRequest<Raw[]>(`agentBuilder/ui/dashboard-versions?${params.toString()}`)
  return Array.isArray(result) ? result.map(mapDashboard) : []
}

export async function getAgentBuilderDashboardSchedules(clientId: string, dashboardRootId: string): Promise<AgentBuilderDashboardSchedule[]> {
  if (!clientId || !dashboardRootId) return []
  const result = await apiRequest<Raw[]>('agentBuilder/ui/dashboard-schedules', {
    query: { clientId, dashboardRootId },
  })
  return asArray<Raw>(result).map(mapDashboardSchedule)
}

export async function getAgentBuilderDashboardSchedule(scheduleId: string): Promise<AgentBuilderDashboardSchedule | null> {
  if (!scheduleId) return null
  const result = await apiRequest<Raw>(`agentBuilder/ui/dashboard-schedules/${scheduleId}`)
  return result ? mapDashboardSchedule(result) : null
}

export async function getAgentBuilderDashboardScheduleRecipients(clientId: string): Promise<AgentBuilderScheduleRecipientOption[]> {
  if (!clientId) return []
  const result = await apiRequest<Raw[]>('agentBuilder/ui/dashboard-schedules/recipients', {
    query: { clientId },
  })
  return asArray<Raw>(result).map(mapScheduleRecipientOption)
}

export async function saveAgentBuilderDashboardSchedule(payload: AgentBuilderDashboardScheduleUpsertRequest): Promise<string | null> {
  return apiRequest<string | null>('agentBuilder/ui/dashboard-schedules/upsert', {
    method: 'POST',
    body: payload,
  })
}

export async function deleteAgentBuilderDashboardSchedule(scheduleId: string): Promise<void> {
  await apiRequest<Raw>(`agentBuilder/ui/dashboard-schedules/${scheduleId}`, {
    method: 'DELETE',
  })
}

export async function resolveAgentBuilderChatScheduleConfirmation(
  agentBuilderChatId: string,
  accept: boolean,
): Promise<AgentBuilderChatStatusResponse> {
  const raw = await apiRequest<Raw>(`agentBuilder/ui/chat/${agentBuilderChatId}/schedule-confirmation`, {
    method: 'POST',
    body: { accept },
  })
  return mapChatStatusResponse(raw)
}

export async function saveAgentBuilderDashboard(payload: SaveAgentBuilderDashboardPayload): Promise<AgentBuilderDashboard> {
  const response = await apiRequest<Raw>('agentBuilder/ui/dashboards', { method: 'POST', body: payload })
  return mapDashboard(response)
}

export type UpdateAgentBuilderDashboardPayload = {
  clientId: string
  dashboardId: string
  platformTypeId?: number
  section?: string | null
}

export type UpdateAgentBuilderDashboardFavoritePayload = {
  clientId: string
  dashboardId: string
  isFavorite: boolean
}

export async function updateAgentBuilderDashboard(
  payload: UpdateAgentBuilderDashboardPayload,
): Promise<AgentBuilderDashboard> {
  const { dashboardId, ...body } = payload
  const response = await apiRequest<Raw>(`agentBuilder/ui/dashboards/${dashboardId}`, {
    method: 'PUT',
    body: { ...body, dashboardId },
  })
  return mapDashboard(response)
}

export async function updateAgentBuilderDashboardFavorite(
  payload: UpdateAgentBuilderDashboardFavoritePayload,
): Promise<AgentBuilderDashboard> {
  const { dashboardId, ...body } = payload
  const response = await apiRequest<Raw>(`agentBuilder/ui/dashboards/${dashboardId}/favorite`, {
    method: 'PUT',
    body,
  })
  return mapDashboard(response)
}

export async function listAgentBuilderDashboardViews(clientId: string, dashboardId: string): Promise<AgentBuilderDashboardView[]> {
  if (!clientId || !dashboardId) return []
  const result = await apiRequest<Raw[]>(`agentBuilder/ui/dashboards/${dashboardId}/views`, {
    query: { clientId },
  })
  return asArray<Raw>(result).map(mapDashboardView)
}

export type CreateAgentBuilderDashboardViewPayload = {
  clientId: string
  dashboardId: string
  name: string
  state: SavedDashboardViewState
  isDefault: boolean
}

export async function createAgentBuilderDashboardView(
  payload: CreateAgentBuilderDashboardViewPayload,
): Promise<AgentBuilderDashboardView> {
  const { dashboardId, state, ...body } = payload
  const response = await apiRequest<Raw>(`agentBuilder/ui/dashboards/${dashboardId}/views`, {
    method: 'POST',
    body: { ...body, stateJson: JSON.stringify(state), schemaVersion: state.schemaVersion },
  })
  return mapDashboardView(response)
}

export type UpdateAgentBuilderDashboardViewPayload = {
  clientId: string
  dashboardId: string
  viewId: string
  name?: string
  state?: SavedDashboardViewState
  isDefault?: boolean
}

export async function updateAgentBuilderDashboardView(
  payload: UpdateAgentBuilderDashboardViewPayload,
): Promise<AgentBuilderDashboardView> {
  const { dashboardId, viewId, state, ...body } = payload
  const response = await apiRequest<Raw>(`agentBuilder/ui/dashboards/${dashboardId}/views/${viewId}`, {
    method: 'PUT',
    body: state ? { ...body, stateJson: JSON.stringify(state), schemaVersion: state.schemaVersion } : body,
  })
  return mapDashboardView(response)
}

export type DeleteAgentBuilderDashboardViewPayload = {
  clientId: string
  dashboardId: string
  viewId: string
}

export async function deleteAgentBuilderDashboardView(payload: DeleteAgentBuilderDashboardViewPayload): Promise<void> {
  await apiRequest<Raw>(`agentBuilder/ui/dashboards/${payload.dashboardId}/views/${payload.viewId}`, {
    method: 'DELETE',
    query: { clientId: payload.clientId },
  })
}

export type SetAgentBuilderDashboardDefaultViewPayload = {
  clientId: string
  dashboardId: string
  /** Null clears the default. */
  viewId: string | null
}

export async function setAgentBuilderDashboardDefaultView(
  payload: SetAgentBuilderDashboardDefaultViewPayload,
): Promise<AgentBuilderDashboardView[]> {
  const { dashboardId, ...body } = payload
  const result = await apiRequest<Raw[]>(`agentBuilder/ui/dashboards/${dashboardId}/views/default`, {
    method: 'PUT',
    body,
  })
  return asArray<Raw>(result).map(mapDashboardView)
}

export async function createAgentBuilderDashboardFromPrompt(
  payload: CreateAgentBuilderDashboardFromPromptPayload,
): Promise<SendAgentBuilderChatMessageResponse> {
  const response = await apiRequest<Raw>('agentBuilder/ui/dashboards/generate', { method: 'POST', body: payload })
  return {
    agentBuilderChatId: asString(response['agentBuilderChatId'] ?? response['AgentBuilderChatId']),
    status: (asString(response['status'] ?? response['Status']) || 'queued') as SendAgentBuilderChatMessageResponse['status'],
  }
}

export async function createAgentBuilderDashboardFromJson(
  payload: CreateAgentBuilderDashboardFromJsonPayload,
): Promise<AgentBuilderDashboard> {
  const response = await apiRequest<Raw>('agentBuilder/ui/dashboards/from-json', { method: 'POST', body: payload })
  return mapDashboard(response)
}

export async function restoreAgentBuilderDashboardVersion(
  payload: RestoreAgentBuilderDashboardVersionPayload,
): Promise<AgentBuilderDashboardVersion> {
  const response = await apiRequest<Raw>('agentBuilder/ui/dashboard-versions/restore', { method: 'POST', body: payload })
  return mapDashboard(response)
}

export async function publishAgentBuilderDashboardVersion(
  payload: PublishAgentBuilderDashboardVersionPayload,
): Promise<AgentBuilderDashboardVersion> {
  const response = await apiRequest<Raw>('agentBuilder/ui/dashboard-versions/publish', { method: 'POST', body: payload })
  return mapDashboard(response)
}

export async function unpublishAgentBuilderDashboardVersion(
  payload: UnpublishAgentBuilderDashboardVersionPayload,
): Promise<AgentBuilderDashboardVersion> {
  const response = await apiRequest<Raw>('agentBuilder/ui/dashboard-versions/unpublish', { method: 'POST', body: payload })
  return mapDashboard(response)
}

export async function deleteAgentBuilderDashboardVersion(
  payload: DeleteAgentBuilderDashboardVersionPayload,
): Promise<void> {
  await apiRequest<Raw>(`agentBuilder/ui/dashboard-versions/${payload.versionId}`, {
    method: 'DELETE',
    query: { clientId: payload.clientId },
  })
}

export async function deleteAgentBuilderDashboard(
  payload: DeleteAgentBuilderDashboardPayload,
): Promise<void> {
  await apiRequest<Raw>(`agentBuilder/ui/dashboards/${payload.rootId}`, {
    method: 'DELETE',
    query: { clientId: payload.clientId },
  })
}

export async function hydrateWidgetData(input: HydrateWidgetInput): Promise<HydratedWidgetResult> {
  const response = await withHydrateWidgetRetry(() => apiRequest<Raw>('agentBuilder/ui/widget/hydrate', {
    method: 'POST',
    body: hydrateWidgetRequestBody(input),
  }))
  return toHydratedWidgetResult(response, input, { withComparison: true })
}

export async function drilldownWidgetData(input: DrilldownWidgetInput): Promise<HydratedWidgetResult> {
  const response = await apiRequest<Raw>('agentBuilder/ui/widget/drilldown', {
    method: 'POST',
    body: drilldownWidgetRequestBody(input),
  })
  return toHydratedWidgetResult(response, input, { withComparison: false })
}

function getFilenameFromContentDisposition(headers: Headers | undefined, fallback: string) {
  const contentDisposition = headers?.get('Content-Disposition')
  if (!contentDisposition) return fallback
  const utfMatch = /filename\*=UTF-8''([^;\n]+)/i.exec(contentDisposition)
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1])
    } catch {
      return utfMatch[1]
    }
  }
  const match = /filename[^;=\n]*=(?:"([^"]*)"|([^;\n]*))/i.exec(contentDisposition)
  const filename = match ? (match[1] || match[2] || '').trim() : ''
  return filename || fallback
}

export async function exportWidgetData(input: ExportWidgetDataInput): Promise<{ blob: Blob; filename: string }> {
  const { blob, headers } = await apiRequestBlob('agentBuilder/ui/widget/export', {
    method: 'POST',
    headers: { Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
    body: exportWidgetRequestBody(input),
  })

  return {
    blob,
    filename: getFilenameFromContentDisposition(headers, `${input.widget.title || 'visuals-data'}.xlsx`),
  }
}

export async function fetchSlicerOptions(input: FetchSlicerOptionsInput): Promise<string[]> {
  const response = await apiRequest<Raw>('agentBuilder/ui/slicerOptions', {
    method: 'POST',
    body: slicerOptionsRequestBody(input),
  })
  return toSlicerOptions(response)
}

export async function getAgentBuilderDrilldownDimensions(
  clientId: string,
  platformTypeId: number,
): Promise<DrilldownHierarchyNode[]> {
  const response = await apiRequest<unknown>('agentBuilder/ui/drilldown/dimensions', {
    query: { clientId, platformTypeId },
  })
  return asArray<Raw>(response)
    .map((raw): DrilldownHierarchyNode | null => {
      const field = asString(raw['field'] ?? raw['Field']).trim()
      const level = asString(raw['level'] ?? raw['Level']).trim()
      if (!field || !level) return null
      const nextRaw = raw['next'] ?? raw['Next']
      const next = typeof nextRaw === 'string' && nextRaw.trim() ? nextRaw.trim() : null
      return { field, level, next }
    })
    .filter((node): node is DrilldownHierarchyNode => node !== null)
}

export async function fetchWidgetInsight(payload: AgentBuilderWidgetInsightPayload): Promise<AgentBuilderInsightDto> {
  const raw = await apiRequest<Raw>('agentBuilder/ui/widget/insight', { method: 'POST', body: payload })
  return mapInsightDto(raw)
}

export async function fetchDashboardGroupInsight(payload: AgentBuilderGroupInsightPayload): Promise<AgentBuilderInsightDto> {
  const raw = await apiRequest<Raw>('agentBuilder/ui/dashboard/insight', { method: 'POST', body: payload })
  return mapInsightDto(raw)
}

export function dashboardPayloadFromDashboard(
  clientId: string,
  dashboard: DashboardDefinition,
  dashboardId?: string | null,
  prompt = 'Dashboard saved',
): SaveAgentBuilderDashboardPayload {
  const section = sanitizeDashboardSection(dashboard.section, dashboard.platformTypeId)
  const sanitizedDashboard = {
    ...dashboard,
    section,
  }
  return {
    dashboardId,
    clientId,
    platformTypeId: sanitizedDashboard.platformTypeId,
    section,
    title: sanitizedDashboard.title,
    prompt,
    dashboardJson: JSON.stringify(sanitizedDashboard),
  }
}
